import { Account } from '@/domain/usecases'
import sentryConfig, { isIgnoredError } from '@/main/config/sentry-config'
import { settings } from '@/main/config/settings'
import { Router as RemixRouter } from '@remix-run/router'
import * as Sentry from '@sentry/react'
import { createBrowserRouter, RouteObject } from 'react-router-dom'

type DOMRouterOpts = NonNullable<Parameters<typeof createBrowserRouter>[1]>

type CaptureContext = {
  tags?: Record<string, string>
  extra?: Record<string, unknown>
  level?: Sentry.SeverityLevel
}

class ErrorMonitor {
  private static environments = ['prod', 'test']

  private _initialized: boolean = false

  public get initialized() {
    return this._initialized
  }

  public startMonitoring(): void {
    if (!ErrorMonitor.environments.includes(settings.APP_ENV)) {
      return
    }

    Sentry.init(sentryConfig)
    this._initialized = true
  }

  public identifyUser = (accountProfile: Account.Model | null) => {
    if (!accountProfile) return
    Sentry.setUser({
      id: accountProfile.id,
      accountType: accountProfile.accountType?.name,
      onboardingCompleted: accountProfile.onboardingCompleted,
      passPlanId: accountProfile.passPlan?.id,
      companyId: accountProfile.company.uniqueId,
      companyName: accountProfile.company.name
    })
  }

  public wrapCreateBrowserRouter = (
    createBrowserRouter: (routes: RouteObject[], opts?: DOMRouterOpts) => RemixRouter
  ) => {
    return Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)
  }

  public captureException = (error: Error | unknown, context?: CaptureContext) => {
    if (isIgnoredError(error)) {
      return
    }

    const capture = (err: Error | unknown) => {
      if (!context) {
        Sentry.captureException(err)
        return
      }
      Sentry.withScope(scope => {
        if (context.tags) scope.setTags(context.tags)
        if (context.extra) scope.setContext('http', context.extra)
        if (context.level) scope.setLevel(context.level)
        Sentry.captureException(err)
      })
    }

    if (error instanceof Error) {
      capture(error)
      return
    }

    if (this.isSerializedAppError(error)) {
      const reconstructedError = new Error(error.message)
      reconstructedError.name = error.name
      capture(reconstructedError)
      return
    }

    capture(error)
  }

  private isSerializedAppError = (
    error: unknown
  ): error is { customError: boolean; message: string; name: string } => {
    return (
      typeof error === 'object' &&
      error !== null &&
      'customError' in error &&
      'message' in error &&
      'name' in error
    )
  }
}

export default new ErrorMonitor()
